import React from 'react';
import { TrialPricingBox, type TrialPlan } from '../onboarding/value/TrialPricingBox';
import { TrialTimelineVisual } from '../onboarding/value/visuals';
import { emphasizeBrand } from '../../lib/emphasizeBrand';
import { SpendingToolsBenefits } from './SpendingToolsBenefits';
import { WebRestoreGuidance } from './WebRestoreGuidance';
import { useReduced } from '@/lib/motion';
import { TERMS_URL, PRIVACY_URL, WEB_PRICES, type TrialDescriptor } from '../../lib/paidLaunch';
import type { NativePackage } from '../../services/purchasesService';

/**
 * The GREEN paywall — the acquisition surface (Justin, 2026-07-21).
 *
 * This is deliberately the same screen the Android carousel closes on, not a
 * lookalike: it renders the carousel's own `TrialTimelineVisual` and
 * `TrialPricingBox` inside the shared funnel column, so someone who sees it
 * before login and someone who sees it after login are looking at one design.
 * Only `SpendingToolsPaywall` owns purchase state — this component is pure
 * presentation and reports intent through its handlers.
 *
 * Two variants, chosen by whether the store will actually grant a trial:
 *
 * - **Trial-eligible → WITH the timeline.** The three-stop timeline is a promise
 *   about a trial ("Today → reminded before it ends → then billed"), so it only
 *   belongs in front of someone who is getting one.
 * - **Not trial-eligible → WITHOUT the timeline.** This is the returning-user
 *   case we solved on 2026-07-21: an existing subscription on a new device, or a
 *   second account on the same store account. The store withholds its intro
 *   offer only from an account that already used it, so these people have
 *   subscribed before — greet them as returning, lead with Restore, and never
 *   show a trial timeline they can't have.
 *
 * NOT for winback or an expired membership on THIS account. That stays on the
 * white in-app treatment (`surface="app"`), which is the surface a member
 * already inside the product expects.
 */

interface SpendingToolsPaywallFunnelProps {
  /** True when the store will actually grant a trial — drives the timeline AND the copy. */
  trialAvailable: boolean;
  /** The trial descriptor for the selected plan, or null. Store-derived on native. */
  trial: TrialDescriptor | null;
  monthly: NativePackage | null;
  annual: NativePackage | null;
  choice: TrialPlan;
  onChoice: (plan: TrialPlan) => void;
  /** Trial-terms wording follows the platform — "1 month" native, "30 days" web. */
  platform: 'native' | 'web';
  ctaLabel: string;
  onPurchase: () => void;
  purchaseDisabled?: boolean;
  /** Native only — the App Store requires a restore path on any purchase surface. */
  onRestore?: () => void;
  errorMessage?: string;
  /** Pre-login mounts: route an existing member to sign-in rather than a second purchase. */
  onSignIn?: () => void;
  /**
   * Post-login web mounts (PaidAccessGate): show the "started your membership
   * on your phone?" line — web has no Restore, so a member whose subscription
   * is an unrestored store purchase is pointed to the app rather than a second,
   * Stripe-billed subscription. The line itself renders nothing on native.
   */
  phoneRestoreGuidance?: boolean;
}

export const SpendingToolsPaywallFunnel: React.FC<SpendingToolsPaywallFunnelProps> = ({
  trialAvailable,
  trial,
  monthly,
  annual,
  choice,
  onChoice,
  platform,
  ctaLabel,
  onPurchase,
  purchaseDisabled,
  onRestore,
  errorMessage,
  onSignIn,
  phoneRestoreGuidance,
}) => {
  const reduced = useReduced();

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex flex-1 flex-col justify-center">
        {/* The timeline is a trial promise — eligible users only. */}
        {trialAvailable && (
          <div className="mb-2">
            <TrialTimelineVisual reduced={reduced} platform={platform} hasTrial />
          </div>
        )}

        <div className="text-center">
          <h2 className="mx-auto max-w-[310px] text-[1.4rem] font-semibold leading-snug text-cloud">
            {trialAvailable ? 'Try it for free.' : 'Pick up where you left off.'}
          </h2>
          {/* Every word on this screen is text-cloud (Justin, 2026-07-21). It
              asks for money, so nothing on it gets to be low-contrast. */}
          <p className="mx-auto mt-2 max-w-[300px] text-[14.5px] leading-relaxed text-cloud">
            {trialAvailable && trial
              ? `${trial.periodLabel} free trial to get you started. Cancel anytime — no pressure.`
              : trialAvailable
                ? 'Connect every account and align your spending with what matters.'
                : emphasizeBrand('Welcome back to enough. Choose the plan that fits — cancel anytime.')}
          </p>
        </div>

        {/* The timeline earns the eligible user's attention; without it, the
            returning user gets the substance instead — what the membership
            actually includes. Same list, same words as the in-app paywall. */}
        {!trialAvailable && (
          <div className="mx-auto mt-6 w-full max-w-[300px]">
            <SpendingToolsBenefits tone="dark" />
          </div>
        )}

        {/* Already a member? Restore is the likelier intent for anyone the store
            says has used its intro offer — lift it above the sell rather than
            leave it in the fine print. Hint only: restore is receipt-verified. */}
        {!trialAvailable && onRestore && (
          <button
            type="button"
            onClick={onRestore}
            className="mt-5 w-full text-center text-[13px] font-medium text-sand/85 transition-colors hover:text-sand"
          >
            Already a member? <span className="underline">Restore</span>
          </button>
        )}
      </div>

      <div className="mt-6">
        <TrialPricingBox
          plan={choice}
          onPlan={onChoice}
          monthlyPrice={WEB_PRICES.monthly.amount}
          annualPrice={WEB_PRICES.annual.amount}
          nativePricing={{ monthly, annual }}
          trial={trial}
        />

        {errorMessage && (
          <div className="mt-4 rounded-xl border border-wildfire/30 bg-wildfire/15 p-3 text-sm text-sand">
            {errorMessage}
          </div>
        )}

        <button
          onClick={onPurchase}
          disabled={purchaseDisabled}
          className="mt-4 w-full rounded-full bg-foliage py-4 text-[15px] font-medium text-growth shadow-[0_10px_30px_rgba(197,160,89,.35)] transition-all hover:-translate-y-0.5 active:scale-[0.98] disabled:opacity-70 disabled:hover:translate-y-0 disabled:cursor-not-allowed"
        >
          {ctaLabel}
        </button>

        {onSignIn && (
          <button
            onClick={onSignIn}
            className="mt-3 w-full text-center text-[13px] text-sand/70 transition-colors hover:text-sand"
          >
            Already a member? <span className="underline">Sign in</span>
          </button>
        )}

        {phoneRestoreGuidance && <WebRestoreGuidance tone="dark" className="mt-3" />}

        {/* Legal row — the App Store requires Terms + Privacy on a purchase
            surface, and Restore alongside them when it isn't already led above. */}
        <div className="mt-3 flex items-center justify-center gap-4 text-[11px] text-sand/55">
          {onRestore && trialAvailable && (
            <>
              <button type="button" onClick={onRestore} className="transition-colors hover:text-sand">
                Restore
              </button>
              <span aria-hidden className="text-sand/25">·</span>
            </>
          )}
          <button
            type="button"
            onClick={() => window.open(TERMS_URL, '_blank')}
            className="transition-colors hover:text-sand"
          >
            Terms
          </button>
          <span aria-hidden className="text-sand/25">·</span>
          <button
            type="button"
            onClick={() => window.open(PRIVACY_URL, '_blank')}
            className="transition-colors hover:text-sand"
          >
            Privacy
          </button>
        </div>
      </div>
    </div>
  );
};
